import React, { useEffect, useState } from 'react';
import availability from '../availability.json';
import { timelessText, datedText, sydneyToday, wordingAfterLoad } from '../utils/availabilityText';
import './AvailabilityStrip.css';

/*
  Availability, shown ONCE on the site: a quiet strip in the homepage hero
  (round 6). Two lines between thin hairlines, then one small grey line.

  availability.json holds SETTINGS, not sentences (see CLAUDE.md):
    assessments / therapy: "open" | "waitlist" | "full"
    initialWeeks, assessmentWeeks, reportAfterAssessmentWeeks
    therapyWait ("6 to 9 months"), therapyCaveat and note (kept, not shown)
    reviewedOn (not shown), liveSourceUrl ("" = live mode off)

  The pre-rendered HTML (Google, no-JavaScript visitors) never names a month:
  "first appointment in about 2 months, report in about 4 months". After the
  page loads the browser swaps in dates from today in Sydney (or the planner's
  live dates if switched on): "first appointment from late December, report
  by late February". Reports are always assessment + reportAfterAssessmentWeeks.
*/

const STATUS = {
  open: { label: 'Taking new enquiries', dot: 'avail-dot--green' },
  waitlist: { label: 'Waitlist open', dot: 'avail-dot--amber' },
  full: { label: 'Books currently full', dot: 'avail-dot--grey' },
};

const AssessmentTimes = ({ text }) => (text.kind === 'dated' ? (
  <>first appointment from <strong>{text.initial.value}</strong>, report by <strong>{text.report.value}</strong></>
) : (
  <>first appointment <strong>{text.initial.value}</strong>, report <strong>{text.reportFromNow}</strong></>
));

const therapyWording = (wait) => (/^about\b/i.test(wait) ? wait : `about ${wait}`);

// anchor: the homepage copy carries id="availability" (the /#availability link
// target) and the small "Current wait times" heading. The /bookings pop-up copy
// has neither: the id stays unique, and the pop-up's own title already says it.
const AvailabilityStrip = ({ anchor = true }) => {
  const [text, setText] = useState(() => ({ ...timelessText(availability) }));

  useEffect(() => {
    // Never during pre-rendering: the saved HTML must stay month-free
    if (window.__PRERENDERING__) return undefined;
    let cancelled = false;
    setText(datedText(availability, sydneyToday()));
    if (availability.liveSourceUrl) {
      wordingAfterLoad(availability, { fetchImpl: window.fetch.bind(window) })
        .then((t) => { if (!cancelled) setText(t); });
    }
    return () => { cancelled = true; };
  }, []);

  const a = STATUS[availability.assessments] || STATUS.open;
  const t = STATUS[availability.therapy] || STATUS.waitlist;

  return (
    <div className="avail-strip" id={anchor ? 'availability' : undefined} data-testid="availability-strip">
      {anchor && <h2 className="avail-heading" data-testid="avail-heading">Current wait times</h2>}
      <dl className="avail-lines">
        <div className="avail-line avail-line--assessments" data-testid="avail-assessments">
          <dt className="avail-label">Assessments</dt>
          <dd className="avail-text">
            <span className={`avail-dot ${a.dot}`} aria-hidden="true" />
            {a.label}
            {availability.assessments !== 'full' && (
              <> · <span data-testid="avail-times"><AssessmentTimes text={text} /></span></>
            )}
          </dd>
        </div>
        <div className="avail-line" data-testid="avail-therapy">
          <dt className="avail-label">Therapy</dt>
          <dd className="avail-text">
            {t.label}
            {availability.therapy !== 'full' && text.therapyWait && (
              <> · <strong>{therapyWording(text.therapyWait)}</strong></>
            )}
          </dd>
        </div>
      </dl>
      <p className="avail-guide">Times are a guide and change through the year.</p>
      {availability.liveSourceUrl && (
        // Space is reserved even before the live data arrives, so nothing jumps
        <p className="avail-guide avail-live" data-testid="availability-live">{text.liveNote}</p>
      )}
    </div>
  );
};

export default AvailabilityStrip;
